import { X, Share2, Link as LinkIcon, ExternalLink, Sparkles, Calendar, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner@2.0.3';
import { NewsArticle } from './RelevantNewsPage';

interface NewsDetailSidebarProps {
  article: NewsArticle | null;
  onClose: () => void;
  onToggleRead: () => void;
}

export function NewsDetailSidebar({ article, onClose, onToggleRead: _onToggleRead }: NewsDetailSidebarProps) {
  if (!article) return null;

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.summary,
        url: article.url,
      }).catch(() => {
        navigator.clipboard.writeText(article.url);
        toast.success('Link copied to clipboard!');
      });
    } else {
      navigator.clipboard.writeText(article.url);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(article.url);
    toast.success('Link copied to clipboard!');
  };

  const getRelevancyColor = (score: number) => {
    if (score >= 90) return 'from-green-500 to-emerald-600';
    if (score >= 75) return 'from-blue-500 to-cyan-600';
    if (score >= 60) return 'from-orange-500 to-amber-600';
    return 'from-gray-500 to-gray-600';
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[70] transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div 
        className="fixed top-0 right-0 bottom-0 w-full lg:w-[60%] bg-white z-[80] shadow-2xl overflow-hidden"
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b bg-white sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                <img 
                  src={article.sourceLogo} 
                  alt={article.sourceName}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-sm">{article.sourceName}</p>
                <p className="text-xs text-gray-500">{article.publishedDate}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={onClose}
              className="h-9 w-9"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Hero Image */}
            <div className="relative h-64 sm:h-80 w-full overflow-hidden">
              <img 
                src={article.image} 
                alt={article.title}
                className="w-full h-full object-cover"
              />
              {/* Relevancy Score */}
              <div className="absolute top-4 right-4">
                <div className={`bg-gradient-to-r ${getRelevancyColor(article.relevancyScore)} text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2`}>
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm">{article.relevancyScore}% Match</span>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 lg:p-8">
              {/* Category Badge */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge className="bg-gradient-to-r from-orange-500 to-purple-600 text-white border-0">
                  {article.category}
                </Badge>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{article.publishedDate}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <BookOpen className="w-3.5 h-3.5" />
                  <span>5 min read</span>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl lg:text-4xl mb-4 sm:mb-6">
                {article.title}
              </h1>

              {/* Summary */}
              <div className="p-4 bg-gradient-to-r from-orange-50 to-purple-50 rounded-lg mb-6">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  {article.summary}
                </p>
              </div>

              {/* Full Content */}
              <div className="prose prose-sm sm:prose max-w-none">
                <div className="text-sm sm:text-base text-gray-700 leading-relaxed space-y-4">
                  {article.fullContent.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={handleShare}
                    className="bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Article
                  </Button>
                  <Button 
                    onClick={handleCopyLink}
                    variant="outline"
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.open(article.url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Read Original
                  </Button>
                </div>
              </div>

              {/* Related Tags */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-3">Related Topics</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="text-xs">Marketing</Badge>
                  <Badge variant="outline" className="text-xs">Social Media</Badge>
                  <Badge variant="outline" className="text-xs">Influencer</Badge>
                  <Badge variant="outline" className="text-xs">Campaign</Badge>
                  <Badge variant="outline" className="text-xs">Analytics</Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}